import { jsPDF } from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, Table, TableRow, TableCell, WidthType, BorderStyle, AlignmentType } from 'docx';
import { saveAs } from 'file-saver';
import { AppInsight } from '@/types';

applyPlugin(jsPDF);

// ============================================================
// PDF Export — Security / EPA / Threat Modelling
// ============================================================

export function exportSentinelPdf(sentinel: AppInsight, appName?: string) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;
  const contentWidth = pageWidth - margin * 2;
  let y = 20;

  const addPageIfNeeded = (needed: number) => {
    if (y + needed > doc.internal.pageSize.getHeight() - 20) {
      doc.addPage();
      y = 20;
    }
  };

  const addTitle = (text: string) => {
    addPageIfNeeded(14);
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text(text, margin, y);
    y += 10;
  };

  const addSectionHeader = (text: string) => {
    addPageIfNeeded(16);
    y += 4;
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(41, 98, 255);
    doc.text(text, margin, y);
    doc.setTextColor(0, 0, 0);
    y += 3;
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, y, pageWidth - margin, y);
    y += 6;
  };

  const addSubHeader = (text: string) => {
    addPageIfNeeded(12);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text(text, margin, y);
    y += 6;
  };

  const addText = (text: string) => {
    if (text == null) return;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const cleaned = stripMarkdown(String(text));
    const lines = doc.splitTextToSize(cleaned, contentWidth);
    for (const line of lines) {
      addPageIfNeeded(6);
      doc.text(line, margin, y);
      y += 5;
    }
    y += 2;
  };

  const addList = (items: string[]) => {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    for (const item of items) {
      const cleaned = stripMarkdown(String(item ?? ''));
      const lines = doc.splitTextToSize(`• ${cleaned}`, contentWidth - 5);
      for (let i = 0; i < lines.length; i++) {
        addPageIfNeeded(6);
        doc.text(lines[i], margin + (i === 0 ? 0 : 3), y);
        y += 5;
      }
    }
    y += 2;
  };

  const addKV = (label: string, value?: string | null) => {
    if (!value) return;
    addText(`${label}: ${value}`);
  };

  const addFindingsAndRecs = (findings?: string[], recommendations?: string[], findingsLabel = 'Findings') => {
    if (findings?.length) { addSubHeader(`${findingsLabel} (${findings.length})`); addList(findings); }
    if (recommendations?.length) { addSubHeader(`Recommendations (${recommendations.length})`); addList(recommendations); }
  };

  // ── Title ──
  addTitle(appName || sentinel.appId || 'Sentinel Report');
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(120, 120, 120);
  doc.text(`App ID: ${sentinel.appId || '—'}`, margin, y);
  y += 5;
  if (sentinel.extractedAt) {
    doc.text(`Extracted: ${new Date(sentinel.extractedAt).toLocaleString()}`, margin, y);
    y += 5;
  }
  doc.setTextColor(0, 0, 0);
  y += 4;

  // ── SECURITY SCORE ──
  const secScore = sentinel.aggregatedSecurityScore || sentinel.securityScore;
  if (secScore) {
    addSectionHeader('Security Score');
    const rating = (secScore as any).rating ? ` (${(secScore as any).rating})` : '';
    addText(`Overall Score: ${secScore.overall}/100${rating}`);
    if (secScore.breakdown) {
      addSubHeader('Breakdown');
      Object.entries(secScore.breakdown).forEach(([key, val]) => {
        const catScore = typeof val === 'object' && val !== null ? (val as any).score ?? val : val;
        const catRating = typeof val === 'object' && val !== null ? (val as any).rating : '';
        addText(`${key.replace(/([A-Z])/g, ' $1').trim()}: ${catScore}/100${catRating ? ` (${catRating})` : ''}`);
      });
    }
    addFindingsAndRecs(secScore.criticalFindings, secScore.topRecommendations, 'Critical Findings');
  }

  // ── THREAT MODELLING ──
  const hasThreat = !!(sentinel.authAnalysis || sentinel.attackSurface || sentinel.strideAnalysis || sentinel.inputValidation || sentinel.cryptoControls || sentinel.secretsManagement || sentinel.networkSecurity || sentinel.dependencyVulnerabilities);
  if (hasThreat) {
    addSectionHeader('Threat Modelling');

    if (sentinel.authAnalysis) {
      addSubHeader('Authentication & Authorization');
      addKV('Protocol', sentinel.authAnalysis.authentication?.protocol);
      addKV('Session Management', sentinel.authAnalysis.authentication?.sessionManagement);
      addKV('MFA Support', sentinel.authAnalysis.authentication?.mfaSupport ? 'Yes' : 'No');
      addKV('Authorization Model', sentinel.authAnalysis.authorization?.model);
      addKV('Roles', sentinel.authAnalysis.authorization?.roles?.join(', '));
      addFindingsAndRecs(sentinel.authAnalysis.vulnerabilities, sentinel.authAnalysis.recommendations, 'Vulnerabilities');
    }

    if (sentinel.attackSurface) {
      addSubHeader('Attack Surface');
      if (sentinel.attackSurface.totalAttackSurface) addText(sentinel.attackSurface.totalAttackSurface);
      if (sentinel.attackSurface.entryPoints?.length) {
        const tableData = sentinel.attackSurface.entryPoints.map(ep => [ep.type, ep.path, ep.authentication, ep.riskLevel, ep.methods?.join(', ') || '—']);
        (doc as any).autoTable({
          startY: y, margin: { left: margin, right: margin },
          head: [['Type', 'Path', 'Auth', 'Risk', 'Methods']],
          body: tableData, styles: { fontSize: 7 }, headStyles: { fillColor: [41, 98, 255] },
        });
        y = (doc as any).lastAutoTable.finalY + 6;
      }
    }

    if (sentinel.cryptoControls) {
      addSubHeader('Cryptographic Controls');
      addKV('Encryption at Rest', sentinel.cryptoControls.encryptionAtRest?.implemented ? `Yes — ${sentinel.cryptoControls.encryptionAtRest.algorithm || ''}` : 'No');
      addKV('TLS Version', sentinel.cryptoControls.encryptionInTransit?.tlsVersion);
      addKV('Key Management', sentinel.cryptoControls.keyManagement?.service);
      addFindingsAndRecs(sentinel.cryptoControls.weaknesses, sentinel.cryptoControls.recommendations, 'Weaknesses');
    }

    if (sentinel.secretsManagement) {
      addSubHeader('Secrets Management');
      addKV('Secrets Store', sentinel.secretsManagement.secretsStore);
      addKV('Rotation Policy', sentinel.secretsManagement.rotationPolicy);
      addFindingsAndRecs(sentinel.secretsManagement.findings, sentinel.secretsManagement.recommendations);
    }

    if (sentinel.networkSecurity) {
      addSubHeader('Network Security');
      addKV('VPC Design', sentinel.networkSecurity.vpcDesign);
      addKV('DDoS Protection', sentinel.networkSecurity.ddosProtection);
      addFindingsAndRecs(sentinel.networkSecurity.findings, sentinel.networkSecurity.recommendations);
    }

    if (sentinel.dependencyVulnerabilities) {
      addSubHeader('Dependency Vulnerabilities');
      addKV('Total Dependencies', String(sentinel.dependencyVulnerabilities.totalDependencies || ''));
      addKV('Scanning', sentinel.dependencyVulnerabilities.hasDependencyScanning ? `Yes (${sentinel.dependencyVulnerabilities.scanningTool || ''})` : 'No');
      if (sentinel.dependencyVulnerabilities.knownVulnerabilities?.length) {
        const tableData = sentinel.dependencyVulnerabilities.knownVulnerabilities.map(v => [
          `${v.package}${v.version ? ` @${v.version}` : ''}`, v.cve || '—', v.severity, v.description || '—', v.fixAvailable ? 'Yes' : 'No',
        ]);
        (doc as any).autoTable({
          startY: y, margin: { left: margin, right: margin },
          head: [['Package', 'CVE', 'Severity', 'Description', 'Fix?']],
          body: tableData, styles: { fontSize: 7 }, headStyles: { fillColor: [41, 98, 255] },
        });
        y = (doc as any).lastAutoTable.finalY + 6;
      }
      addFindingsAndRecs(undefined, sentinel.dependencyVulnerabilities.recommendations);
    }
  }

  // ── EPA & PRIVACY ──
  const hasEpa = !!(sentinel.piiInventory?.length || sentinel.personalDataFlows?.length || sentinel.dataRetention || sentinel.thirdPartyDataSharing?.length);
  if (hasEpa) {
    addSectionHeader('EPA & Privacy');
    if (sentinel.piiInventory?.length) { addSubHeader(`PII Inventory (${sentinel.piiInventory.length})`); addList(sentinel.piiInventory); }
    if (sentinel.personalDataFlows?.length) { addSubHeader('Personal Data Flows'); sentinel.personalDataFlows.forEach((f, i) => addText(`Flow ${i + 1}: ${f}`)); }
    if (sentinel.dataRetention) { addSubHeader('Data Retention'); addKV('Has Retention Policy', sentinel.dataRetention.hasRetentionPolicy ? 'Yes' : 'No'); addFindingsAndRecs(sentinel.dataRetention.findings, sentinel.dataRetention.recommendations); }
    if (sentinel.thirdPartyDataSharing?.length) { addSubHeader('Third-Party Data Sharing'); addList(sentinel.thirdPartyDataSharing); }
  }

  // ── COMPLIANCE ──
  const hasCompliance = !!(sentinel.complianceFrameworks || sentinel.auditTrail || sentinel.loggingMonitoring);
  if (hasCompliance) {
    addSectionHeader('Compliance');
    if (sentinel.complianceFrameworks?.applicable?.length) {
      addSubHeader('Applicable Frameworks');
      sentinel.complianceFrameworks.applicable.forEach(fw => addText(`${fw.framework} [${fw.complianceLevel}]: ${fw.reason}`));
      addFindingsAndRecs(undefined, sentinel.complianceFrameworks.recommendations);
    }
    if (sentinel.loggingMonitoring) {
      addSubHeader('Logging & Monitoring');
      addKV('Log Storage', sentinel.loggingMonitoring.logStorage);
      addKV('Log Retention', sentinel.loggingMonitoring.logRetention);
      addFindingsAndRecs(sentinel.loggingMonitoring.findings, sentinel.loggingMonitoring.recommendations);
    }
    if (sentinel.auditTrail) {
      addSubHeader('Audit Trail');
      addKV('Has Audit Trail', sentinel.auditTrail.hasAuditTrail ? 'Yes' : 'No');
      addFindingsAndRecs(sentinel.auditTrail.gaps, sentinel.auditTrail.recommendations, 'Gaps');
    }
  }

  // ── Footer on each page ──
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Page ${i} of ${pageCount} — Generated ${new Date().toLocaleString()}`,
      pageWidth / 2,
      doc.internal.pageSize.getHeight() - 10,
      { align: 'center' }
    );
  }

  const filename = `${(appName || sentinel.appId || 'sentinel').replace(/[^a-zA-Z0-9_-]/g, '_')}_sentinel.pdf`;
  doc.save(filename);
}

// ============================================================
// DOCX Export — Security / EPA / Threat Modelling
// ============================================================

const DOCX_BORDER = { style: BorderStyle.SINGLE, size: 1, color: 'CCCCCC' };
const DOCX_BORDERS = { top: DOCX_BORDER, bottom: DOCX_BORDER, left: DOCX_BORDER, right: DOCX_BORDER };

function docxHeading(text: string, level: (typeof HeadingLevel)[keyof typeof HeadingLevel]) {
  return new Paragraph({ text, heading: level, spacing: { before: 300, after: 120 } });
}

function docxParagraph(text: string, bold = false) {
  return new Paragraph({
    children: [new TextRun({ text: stripMarkdown(text), size: 22, font: 'Calibri', bold })],
    spacing: { after: 100 },
  });
}

function docxBullet(text: string) {
  return new Paragraph({
    children: [new TextRun({ text: stripMarkdown(text), size: 22, font: 'Calibri' })],
    bullet: { level: 0 },
    spacing: { after: 40 },
  });
}

function docxKeyValue(label: string, value: string) {
  return new Paragraph({
    children: [
      new TextRun({ text: `${label}: `, size: 22, font: 'Calibri', bold: true }),
      new TextRun({ text: stripMarkdown(value), size: 22, font: 'Calibri' }),
    ],
    spacing: { after: 60 },
  });
}

function docxTableRow(cells: string[], header = false) {
  return new TableRow({
    children: cells.map(text => new TableCell({
      children: [new Paragraph({
        children: [new TextRun({ text, size: 20, font: 'Calibri', bold: header })],
        spacing: { after: 0 },
      })],
      borders: DOCX_BORDERS,
      width: { size: Math.floor(9000 / cells.length), type: WidthType.DXA },
    })),
  });
}

function docxFindingsAndRecs(children: (Paragraph | Table)[], findings?: string[], recommendations?: string[], findingsLabel = 'Findings') {
  if (findings?.length) {
    children.push(docxHeading(`${findingsLabel} (${findings.length})`, HeadingLevel.HEADING_3));
    findings.forEach(f => children.push(docxBullet(f)));
  }
  if (recommendations?.length) {
    children.push(docxHeading(`Recommendations (${recommendations.length})`, HeadingLevel.HEADING_3));
    recommendations.forEach(r => children.push(docxBullet(r)));
  }
}

export async function exportSentinelDocx(sentinel: AppInsight, appNameOverride?: string) {
  const children: (Paragraph | Table)[] = [];
  const appName = appNameOverride || sentinel.appId || 'Sentinel';

  // ── Title ──
  children.push(new Paragraph({
    children: [new TextRun({ text: appName, size: 48, font: 'Calibri', bold: true, color: '0B6BCB' })],
    spacing: { after: 40 },
  }));
  children.push(new Paragraph({
    children: [new TextRun({ text: `Generated ${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`, size: 18, font: 'Calibri', color: '999999' })],
    spacing: { after: 300 },
  }));

  // ── Security Score ──
  const secScoreDocx = sentinel.aggregatedSecurityScore || sentinel.securityScore;
  if (secScoreDocx) {
    children.push(docxHeading('Security Score', HeadingLevel.HEADING_1));
    const ratingDocx = (secScoreDocx as any).rating ? ` (${(secScoreDocx as any).rating})` : '';
    children.push(docxKeyValue('Overall Score', `${secScoreDocx.overall}/100${ratingDocx}`));
    if (secScoreDocx.breakdown) {
      children.push(docxHeading('Breakdown', HeadingLevel.HEADING_2));
      Object.entries(secScoreDocx.breakdown).forEach(([key, val]) => {
        const catScore = typeof val === 'object' && val !== null ? (val as any).score ?? val : val;
        const catRating = typeof val === 'object' && val !== null ? (val as any).rating : '';
        children.push(docxKeyValue(key.replace(/([A-Z])/g, ' $1').trim(), `${catScore}/100${catRating ? ` (${catRating})` : ''}`));
      });
    }
    docxFindingsAndRecs(children, secScoreDocx.criticalFindings, secScoreDocx.topRecommendations, 'Critical Findings');
  }

  // ── Threat Modelling ──
  if (sentinel.authAnalysis || sentinel.attackSurface || sentinel.cryptoControls || sentinel.secretsManagement || sentinel.networkSecurity || sentinel.dependencyVulnerabilities) {
    children.push(docxHeading('Threat Modelling', HeadingLevel.HEADING_1));

    if (sentinel.authAnalysis) {
      children.push(docxHeading('Authentication & Authorization', HeadingLevel.HEADING_2));
      if (sentinel.authAnalysis.authentication?.protocol) children.push(docxKeyValue('Protocol', sentinel.authAnalysis.authentication.protocol));
      if (sentinel.authAnalysis.authorization?.model) children.push(docxKeyValue('Authorization Model', sentinel.authAnalysis.authorization.model));
      if (sentinel.authAnalysis.authorization?.roles?.length) children.push(docxKeyValue('Roles', sentinel.authAnalysis.authorization.roles.join(', ')));
      docxFindingsAndRecs(children, sentinel.authAnalysis.vulnerabilities, sentinel.authAnalysis.recommendations, 'Vulnerabilities');
    }

    if (sentinel.attackSurface) {
      children.push(docxHeading('Attack Surface', HeadingLevel.HEADING_2));
      if (sentinel.attackSurface.totalAttackSurface) children.push(docxParagraph(sentinel.attackSurface.totalAttackSurface));
      if (sentinel.attackSurface.entryPoints?.length) {
        const rows = [
          docxTableRow(['Type', 'Path', 'Auth', 'Risk', 'Methods'], true),
          ...sentinel.attackSurface.entryPoints.map(ep => docxTableRow([ep.type, ep.path, ep.authentication, ep.riskLevel, ep.methods?.join(', ') || '—'])),
        ];
        children.push(new Table({ rows, width: { size: 9000, type: WidthType.DXA } }));
      }
    }

    if (sentinel.cryptoControls) {
      children.push(docxHeading('Cryptographic Controls', HeadingLevel.HEADING_2));
      if (sentinel.cryptoControls.encryptionAtRest) children.push(docxKeyValue('Encryption at Rest', sentinel.cryptoControls.encryptionAtRest.implemented ? `Yes — ${sentinel.cryptoControls.encryptionAtRest.algorithm || ''}` : 'No'));
      if (sentinel.cryptoControls.encryptionInTransit?.tlsVersion) children.push(docxKeyValue('TLS Version', sentinel.cryptoControls.encryptionInTransit.tlsVersion));
      docxFindingsAndRecs(children, sentinel.cryptoControls.weaknesses, sentinel.cryptoControls.recommendations, 'Weaknesses');
    }

    if (sentinel.secretsManagement) {
      children.push(docxHeading('Secrets Management', HeadingLevel.HEADING_2));
      if (sentinel.secretsManagement.secretsStore) children.push(docxKeyValue('Secrets Store', sentinel.secretsManagement.secretsStore));
      docxFindingsAndRecs(children, sentinel.secretsManagement.findings, sentinel.secretsManagement.recommendations);
    }

    if (sentinel.dependencyVulnerabilities) {
      children.push(docxHeading('Dependency Vulnerabilities', HeadingLevel.HEADING_2));
      if (sentinel.dependencyVulnerabilities.knownVulnerabilities?.length) {
        const rows = [
          docxTableRow(['Package', 'CVE', 'Severity', 'Description', 'Fix?'], true),
          ...sentinel.dependencyVulnerabilities.knownVulnerabilities.map(v =>
            docxTableRow([`${v.package}${v.version ? ` @${v.version}` : ''}`, v.cve || '—', v.severity, v.description || '—', v.fixAvailable ? 'Yes' : 'No'])
          ),
        ];
        children.push(new Table({ rows, width: { size: 9000, type: WidthType.DXA } }));
      }
      docxFindingsAndRecs(children, undefined, sentinel.dependencyVulnerabilities.recommendations);
    }
  }

  // ── EPA & Privacy ──
  if (sentinel.piiInventory?.length || sentinel.personalDataFlows?.length || sentinel.thirdPartyDataSharing?.length) {
    children.push(docxHeading('EPA & Privacy', HeadingLevel.HEADING_1));
    if (sentinel.piiInventory?.length) {
      children.push(docxHeading(`PII Inventory (${sentinel.piiInventory.length})`, HeadingLevel.HEADING_2));
      sentinel.piiInventory.forEach(item => children.push(docxBullet(item)));
    }
    if (sentinel.personalDataFlows?.length) {
      children.push(docxHeading('Personal Data Flows', HeadingLevel.HEADING_2));
      sentinel.personalDataFlows.forEach((flow, i) => children.push(docxParagraph(`Flow ${i + 1}: ${flow}`)));
    }
    if (sentinel.thirdPartyDataSharing?.length) {
      children.push(docxHeading('Third-Party Data Sharing', HeadingLevel.HEADING_2));
      sentinel.thirdPartyDataSharing.forEach(item => children.push(docxBullet(item)));
    }
  }

  // ── Compliance ──
  if (sentinel.complianceFrameworks || sentinel.auditTrail || sentinel.loggingMonitoring) {
    children.push(docxHeading('Compliance', HeadingLevel.HEADING_1));
    if (sentinel.complianceFrameworks?.applicable?.length) {
      children.push(docxHeading('Applicable Frameworks', HeadingLevel.HEADING_2));
      sentinel.complianceFrameworks.applicable.forEach(fw => {
        children.push(docxParagraph(`${fw.framework} [${fw.complianceLevel}]: ${fw.reason}`, true));
      });
      docxFindingsAndRecs(children, undefined, sentinel.complianceFrameworks.recommendations);
    }
    if (sentinel.loggingMonitoring) {
      children.push(docxHeading('Logging & Monitoring', HeadingLevel.HEADING_2));
      if (sentinel.loggingMonitoring.logStorage) children.push(docxKeyValue('Log Storage', sentinel.loggingMonitoring.logStorage));
      if (sentinel.loggingMonitoring.logRetention) children.push(docxKeyValue('Log Retention', sentinel.loggingMonitoring.logRetention));
      docxFindingsAndRecs(children, sentinel.loggingMonitoring.findings, sentinel.loggingMonitoring.recommendations);
    }
    if (sentinel.auditTrail) {
      children.push(docxHeading('Audit Trail', HeadingLevel.HEADING_2));
      children.push(docxKeyValue('Has Audit Trail', sentinel.auditTrail.hasAuditTrail ? 'Yes' : 'No'));
      docxFindingsAndRecs(children, sentinel.auditTrail.gaps, sentinel.auditTrail.recommendations, 'Gaps');
    }
  }

  // ── Generate & Download ──
  const doc = new Document({
    sections: [{ children }],
    styles: {
      default: {
        document: { run: { font: 'Calibri', size: 22 } },
        heading1: { run: { font: 'Calibri', size: 32, bold: true, color: '0B6BCB' } },
        heading2: { run: { font: 'Calibri', size: 26, bold: true, color: '333333' } },
      },
    },
  });

  const blob = await Packer.toBlob(doc);
  const filename = `${appName.replace(/[^a-zA-Z0-9_-]/g, '_')}_sentinel.docx`;
  saveAs(blob, filename);
}

// ============================================================
// Helpers
// ============================================================

function stripMarkdown(text: string): string {
  const str = String(text ?? '');
  return str
    .replace(/#{1,6}\s+/g, '')       // headers
    .replace(/\*\*(.+?)\*\*/g, '$1') // bold
    .replace(/\*(.+?)\*/g, '$1')     // italic
    .replace(/__(.+?)__/g, '$1')     // bold
    .replace(/_(.+?)_/g, '$1')       // italic
    .replace(/`(.+?)`/g, '$1')       // inline code
    .replace(/```[\s\S]*?```/g, '')   // code blocks
    .replace(/\[(.+?)\]\(.+?\)/g, '$1') // links
    .replace(/^\s*[-*+]\s+/gm, '• ') // list items
    .replace(/^\s*\d+\.\s+/gm, '')   // numbered lists
    .trim();
}
